import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Search, CheckCircle2, AlertCircle, XCircle, Clock } from 'lucide-react';
import { AppShell } from '@/components/app/AppShell';
import { AdminCompany, useAdminCompanies, useDecideVerification } from '@/lib/admin';
import { useDebounce } from '@/hooks';
import { confirm } from '@/components/ui/ConfirmDialog';
import { countryFlag } from '@/lib/countries';

const STATUS_ICON = {
  verified:        { Icon: CheckCircle2, className: 'text-emerald-600' },
  pending:         { Icon: Clock,        className: 'text-amber-600' },
  pending_review:  { Icon: AlertCircle,  className: 'text-amber-600' },
  rejected:        { Icon: XCircle,      className: 'text-rose-600' },
} as const;

export function AdminCompaniesPage() {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 300);
  const companies = useAdminCompanies(debouncedSearch);

  return (
    <AppShell>
      <div className="px-6 md:px-10 py-8 max-w-7xl mx-auto space-y-6">
        <header>
          <p className="text-xs uppercase tracking-[0.2em] text-primary font-semibold">{t('admin.eyebrow')}</p>
          <h1 className="display serif-jp text-3xl md:text-4xl font-semibold tracking-tight">{t('admin.companies.title')}</h1>
          <p className="mt-1 text-sm text-muted-foreground">{t('admin.companies.subtitle')}</p>
        </header>

        <div className="relative">
          <Search className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('admin.companies.searchPlaceholder')}
            className="w-full h-11 rounded-xl border border-border bg-card ps-10 pe-3 text-sm outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
          />
        </div>

        <div className="rounded-2xl border border-border bg-card shadow-soft overflow-hidden">
          {companies.isLoading ? (
            <p className="px-5 py-8 text-sm text-muted-foreground">{t('common.loading')}</p>
          ) : companies.isError ? (
            <div className="px-5 py-12 text-center">
              <p className="text-sm font-medium text-rose-600">
                {t('admin.companies.loadError', { defaultValue: "Couldn't load companies. Try again." })}
              </p>
              <button
                onClick={() => companies.refetch()}
                className="mt-3 inline-flex h-9 items-center px-4 rounded-lg bg-primary/10 text-primary text-sm font-semibold hover:bg-primary/20"
              >
                {t('common.retry', { defaultValue: 'Retry' })}
              </button>
            </div>
          ) : (companies.data ?? []).length === 0 ? (
            <p className="px-5 py-12 text-center text-sm text-muted-foreground">{t('admin.companies.empty')}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-muted/40 text-start">
                  <tr>
                    <th className="px-4 py-3 font-semibold">{t('admin.companies.col.name')}</th>
                    <th className="px-4 py-3 font-semibold">{t('admin.companies.col.plan')}</th>
                    <th className="px-4 py-3 font-semibold">{t('admin.companies.col.users')}</th>
                    <th className="px-4 py-3 font-semibold">{t('admin.companies.col.status')}</th>
                    <th className="px-4 py-3 font-semibold">{t('admin.companies.col.actions')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {(companies.data ?? []).map((c, i) => (
                    <motion.tr
                      key={c.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: Math.min(i * 0.02, 0.3) }}
                      className="hover:bg-muted/20 transition"
                    >
                      <td className="px-4 py-3">
                        <p className="font-semibold inline-flex items-center gap-2">
                          <span>{countryFlag(c.hqCountryCode)}</span>{c.name}
                        </p>
                        <p className="text-xs text-muted-foreground num">{c.id.slice(0, 8)}…</p>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-md bg-muted">
                          {c.plan}
                        </span>
                      </td>
                      <td className="px-4 py-3 num">{c.userCount}</td>
                      <td className="px-4 py-3">
                        <StatusCell status={c.verificationStatus} />
                      </td>
                      <td className="px-4 py-3">
                        <VerificationActions company={c} />
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </AppShell>
  );
}

function StatusCell({ status }: { status: AdminCompany['verificationStatus'] }) {
  const { t } = useTranslation();
  const { Icon, className } = STATUS_ICON[status];
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold ${className}`}>
      <Icon className="h-3.5 w-3.5" />
      {t(`admin.companies.status.${status}`)}
    </span>
  );
}

function VerificationActions({ company }: { company: AdminCompany }) {
  const { t } = useTranslation();
  const decide = useDecideVerification();
  if (company.verificationStatus === 'verified' || company.verificationStatus === 'rejected') {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <div className="flex items-center gap-2">
      <button
        onClick={async () => {
          const ok = await confirm({
            title: t('admin.companies.confirmApproveTitle'),
            description: t('admin.companies.confirmApproveBody', { name: company.name }),
            confirmLabel: t('admin.approve'),
          });
          if (ok) decide.mutate({ companyId: company.id, status: 'verified' });
        }}
        className="text-xs font-semibold text-emerald-600 hover:underline"
      >
        {t('admin.approve')}
      </button>
      <button
        onClick={async () => {
          const ok = await confirm({
            title: t('admin.companies.confirmRejectTitle'),
            description: t('admin.companies.confirmRejectBody', { name: company.name }),
            tone: 'destructive',
            confirmLabel: t('admin.reject'),
          });
          if (ok) decide.mutate({ companyId: company.id, status: 'rejected' });
        }}
        className="text-xs font-semibold text-rose-600 hover:underline"
      >
        {t('admin.reject')}
      </button>
    </div>
  );
}
